import React, { createContext, useState } from 'react';
import { useClientUser } from '@/hooks';
import {
    useRoleScope,
    useDeliveryProfileSelection,
    useProfileSubregionsMutations,
    useCarrierDirectory,
    useShipperDirectory,
    useSubregionImportExport,
} from './hooks';
import { useSubregionState, useSubregionCallbacks } from '../hooks';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const { isOnwardAdmin } = useClientUser();
    const scope = useRoleScope();

    const [selected, selectObject] = useState({});
    const [hovered, hoverObject] = useState({});
    const [filter, setFilter] = useState({});
    const [collapsed, setCollapsed] = useState({});
    const [notification, setNotification] = useState({});

    const showError = (message) => setNotification({ severity: 'error', message });
    const showSuccess = (message) => setNotification({ severity: 'success', message });

    const profileSelection = useDeliveryProfileSelection(scope, { onError: showError });
    const subregionMutations = useProfileSubregionsMutations(profileSelection.deliveryProfileId, {
        onError: showError,
    });
    const carriers = useCarrierDirectory(scope);

    const shipperDirectory = useShipperDirectory(scope, { onError: showError });

    const subregions = profileSelection.deliveryProfile?.subregions || [];

    const carrierEnrichments = {
        carriersById: Object.fromEntries(carriers.allCarriers.map((c) => [c.client_id, c])),
        mmCarriersById: Object.fromEntries(
            carriers.carriersForLeg('middle_mile_carrier_id').map((c) => [c.client_id, c])
        ),
        warehouseCarriersById: Object.fromEntries(
            carriers
                .carriersForLeg('warehouse_carrier_id')
                .filter((c) => !!c.locations?.length)
                .map((c) => [c.client_id, c])
        ),
    };

    const importExport = useSubregionImportExport({
        subregions,
        deliveryProfile: profileSelection.deliveryProfile,
        deliveryProfileId: profileSelection.deliveryProfileId,
        isOnwardAdmin,
        carriers: carrierEnrichments,
        uploadSubregions: subregionMutations.uploadSubregions,
        onError: showError,
        onSuccess: showSuccess,
    });

    const state = useSubregionState({ subregions, selected, hovered, filter });

    // useSubregionCallbacks is shared with the standalone Subregions page; its mutation half
    // (createSubregion, deleteSubregion, updateSubregion, importSubregions, addZips, removeZips,
    // removeZip) requires `state.query`/`state.queryVariables` to wire cache updates, which
    // AutoAssign doesn't pass — those mutations are owned by useProfileSubregionsMutations here.
    // Pull only the stateless toggle / naming helpers; never spread the whole object.
    const { toggleSubregion, toggleZips, toggleSubregionHover, toggleZipHover, genName, genOwner, genColor } =
        useSubregionCallbacks(
            { subregions },
            // setLoading is unused here — the toggle/naming helpers we destructure don't write
            // loading state. The mutation half of useSubregionCallbacks does, but it's not used:
            // useProfileSubregionsMutations owns mutation state and surfaces it via subregionMutations.loading.
            { setLoading: () => {}, selectObject, hoverObject, setNotification }
        );

    return (
        <Context.Provider
            value={{
                state: {
                    ...state,
                    collapsed,
                    notification,
                    deliveryProfileId: profileSelection.deliveryProfileId,
                    deliveryProfiles: profileSelection.deliveryProfiles,
                    deliveryProfile: profileSelection.deliveryProfile,
                    subregions,
                    scope,
                    shippers: shipperDirectory.shippers,
                    assignmentsByShipper: shipperDirectory.assignmentsByShipper,
                    carriersForLeg: carriers.carriersForLeg,
                    allCarriers: carriers.allCarriers,
                },
                loading: {
                    init: profileSelection.loading,
                    subregions: subregionMutations.loading?.subregions,
                    zips: subregionMutations.loading?.zips,
                    carriers: subregionMutations.loading?.carriers,
                    // SubregionList reads loading.import to disable the import button + show a
                    // spinner. The actual upload runs through useProfileSubregionsMutations,
                    // whose subregions flag combines insert + delete + upload. Reusing it here
                    // is slightly over-eager (any subregion mutation disables the button) but
                    // keeps the import affordance correct without a separate flag.
                    import: subregionMutations.loading?.subregions,
                },
                callbacks: {
                    toggleSubregion,
                    toggleZips,
                    toggleSubregionHover,
                    toggleZipHover,
                    genName,
                    genOwner,
                    genColor,
                    setNotification,
                    selectObject,
                    hoverObject,
                    setFilter,
                    setCollapsed,
                    selectDeliveryProfile: profileSelection.selectDeliveryProfile,
                    upsertDeliveryProfile: profileSelection.upsertProfile,
                    createSubregion: subregionMutations.createSubregion,
                    updateSubregion: subregionMutations.updateSubregion,
                    deleteSubregion: subregionMutations.deleteSubregion,
                    addZips: subregionMutations.addZips,
                    removeZips: subregionMutations.removeZips,
                    removeZip: subregionMutations.removeZip,
                    addCarrierMapping: subregionMutations.addCarrierMapping,
                    applyToShipper: shipperDirectory.applyToShipper,
                    importSubregions: importExport.importSubregions,
                    exportSubregions: importExport.exportSubregions,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
